import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const DAY = 86400000;

/** @param {Date} date @returns {string} */
const dateKey = (date) => date.toISOString().slice(0, 10);

/** @param {number} year @param {number} month @param {number} day @returns {Date} */
const civilDate = (year, month, day) => new Date(Date.UTC(year, month - 1, day, 12));

/** @param {Date} date @returns {Date} */
const nextDate = (date) => new Date(date.getTime() + DAY);

/**
 * USPS operating holidays: a Saturday holiday stays Saturday; a Sunday holiday is also observed Monday.
 * @param {number} year
 * @returns {Set<string>}
 */
export function getUSPSHolidays(year) {
  const holidays = new Set();
  const addFixed = (month, day) => {
    const date = civilDate(year, month, day);
    holidays.add(dateKey(date));
    if (date.getUTCDay() === 0) holidays.add(dateKey(nextDate(date)));
  };
  const addMonday = (month, occurrence) => {
    const first = civilDate(year, month, 1);
    holidays.add(dateKey(civilDate(year, month, 1 + ((8 - first.getUTCDay()) % 7) + 7 * (occurrence - 1))));
  };

  addFixed(1, 1);
  addMonday(1, 3);
  addMonday(2, 3);
  const mayEnd = civilDate(year, 5, 31);
  holidays.add(dateKey(civilDate(year, 5, 31 - ((mayEnd.getUTCDay() + 6) % 7))));
  addFixed(6, 19);
  addFixed(7, 4);
  addMonday(9, 1);
  addMonday(10, 2);
  addFixed(11, 11);
  const november = civilDate(year, 11, 1);
  holidays.add(dateKey(civilDate(year, 11, 1 + ((11 - november.getUTCDay()) % 7) + 21)));
  addFixed(12, 25);
  return holidays;
}

/**
 * Calculate with store-local civil dates, independently of the shopper's timezone or daylight saving changes.
 * Dispatch is Monday-Friday; transit is Monday-Saturday. Dispatch day is not transit day one.
 * Merchant closure dates affect dispatch only. Invalid settings fail closed without an estimate.
 * @param {Date} now
 * @param {{timeZone: string, cutoffHour: number, cutoffMinute: number, transitDays: number, processingDays: number, closures?: string}} config
 * @returns {{orderDate: string, shipDate: string, deliveryDate: string, shipRelative: string} | null}
 */
export function calculateDeliveryEstimate(now, config) {
  try {
    if (!(now instanceof Date) || !Number.isFinite(now.getTime())) return null;
    const { timeZone, cutoffHour, cutoffMinute, transitDays, processingDays } = config;
    if (
      !timeZone ||
      !Number.isInteger(cutoffHour) || cutoffHour < 0 || cutoffHour > 23 ||
      !Number.isInteger(cutoffMinute) || cutoffMinute < 0 || cutoffMinute > 59 ||
      !Number.isInteger(transitDays) || transitDays < 1 || transitDays > 30 ||
      !Number.isInteger(processingDays) || processingDays < 0 || processingDays > 30
    ) return null;

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
    }).formatToParts(now);
    const values = Object.fromEntries(parts.map((part) => [part.type, Number(part.value)]));
    const order = civilDate(values.year, values.month, values.day);
    const closures = new Set();
    for (const value of (config.closures || '').split(/[\s,]+/).filter(Boolean)) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
      const [year, month, day] = value.split('-').map(Number);
      if (dateKey(civilDate(year, month, day)) !== value) return null;
      closures.add(value);
    }
    const calendars = new Map();
    const isHoliday = (date) => {
      const year = date.getUTCFullYear();
      if (!calendars.has(year)) calendars.set(year, getUSPSHolidays(year));
      return calendars.get(year).has(dateKey(date));
    };
    const canDispatch = (date) => date.getUTCDay() > 0 && date.getUTCDay() < 6 && !isHoliday(date) && !closures.has(dateKey(date));
    const canTransit = (date) => date.getUTCDay() !== 0 && !isHoliday(date);
    let ship = order;
    if (values.hour * 60 + values.minute >= cutoffHour * 60 + cutoffMinute) ship = nextDate(ship);
    let searched = 0;
    while (!canDispatch(ship)) {
      if (++searched > 366) return null;
      ship = nextDate(ship);
    }
    for (let remaining = processingDays; remaining > 0;) {
      ship = nextDate(ship);
      if (++searched > 366) return null;
      if (canDispatch(ship)) remaining--;
    }
    let delivery = ship;
    for (let remaining = transitDays; remaining > 0;) {
      delivery = nextDate(delivery);
      if (++searched > 732) return null;
      if (canTransit(delivery)) remaining--;
    }
    const orderDate = dateKey(order);
    const shipDate = dateKey(ship);
    return {
      orderDate,
      shipDate,
      deliveryDate: dateKey(delivery),
      shipRelative: shipDate === orderDate ? 'today' : shipDate === dateKey(nextDate(order)) ? 'tomorrow' : 'date',
    };
  } catch {
    return null;
  }
}

/**
 * @extends {Component<{deliveryDate: HTMLTimeElement, dispatch: HTMLElement}>}
 */
class BHGDeliveryEstimate extends Component {
  requiredRefs = ['deliveryDate', 'dispatch'];
  #controller;
  #timer;
  #pending = false;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    clearInterval(this.#timer);
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    if (!this.#isMainProduct()) {
      this.hidden = true;
      return;
    }
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantSelected, this.#onSelected, { signal });
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onUpdated, { signal });
    document.addEventListener('visibilitychange', this.#refresh, { signal });
    window.addEventListener('pageshow', this.#refresh, { signal });
    this.#refresh();
    // Refresh a long-open page across the minute-based cutoff or midnight without a countdown.
    this.#timer = setInterval(this.#refresh, 60000);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    clearInterval(this.#timer);
  }

  #isMainProduct() {
    return !!this.closest('main .product-information') && !this.closest('quick-add-dialog, quick-add-component, product-card, featured-product-information');
  }

  #isMainEvent(event) {
    const picker = event.target instanceof Element ? event.target.closest('variant-picker') : null;
    return !!picker && picker.dataset.templateProductMatch === 'true' &&
      picker.dataset.sectionId === this.dataset.sectionId &&
      !picker.closest('quick-add-dialog, quick-add-component, product-card');
  }

  #onSelected = (event) => {
    if (!this.#isMainEvent(event)) return;
    this.#pending = true;
    this.hidden = true;
  };

  #onUpdated = (event) => {
    if (!this.#isMainEvent(event)) return;
    const incoming = event.detail?.data?.html?.getElementById(this.id);
    if (!incoming || event.detail?.resource?.available !== true) {
      this.dataset.eligible = 'false';
    } else {
      // Use the section response for stock policy, shipping requirement, and merchant exclusions.
      for (const [key, value] of Object.entries(incoming.dataset)) this.dataset[key] = value;
    }
    this.#pending = false;
    this.#refresh();
  };

  #refresh = () => {
    if (this.#pending || this.dataset.eligible !== 'true' || !this.#isMainProduct()) {
      this.hidden = true;
      return;
    }
    if ([this.dataset.cutoffHour, this.dataset.cutoffMinute, this.dataset.transitDays, this.dataset.processingDays]
      .some((value) => value == null || value.trim() === '')) {
      this.hidden = true;
      return;
    }
    const estimate = calculateDeliveryEstimate(new Date(), {
      timeZone: this.dataset.timeZone || '',
      cutoffHour: Number(this.dataset.cutoffHour),
      cutoffMinute: Number(this.dataset.cutoffMinute),
      transitDays: Number(this.dataset.transitDays),
      processingDays: Number(this.dataset.processingDays),
      closures: this.dataset.closures,
    });
    if (!estimate) {
      this.hidden = true;
      return;
    }
    const locale = document.documentElement.lang || 'en';
    let format;
    try {
      format = new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
    } catch {
      format = new Intl.DateTimeFormat('en', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
    }
    const formatDate = (key) => format.format(new Date(`${key}T12:00:00Z`));
    this.refs.deliveryDate.dateTime = estimate.deliveryDate;
    this.refs.deliveryDate.textContent = formatDate(estimate.deliveryDate);
    this.refs.dispatch.textContent = estimate.shipRelative === 'today'
      ? this.dataset.shipsToday || ''
      : estimate.shipRelative === 'tomorrow'
        ? this.dataset.shipsTomorrow || ''
        : (this.dataset.shipsOn || '').replace('__BHG_DATE__', formatDate(estimate.shipDate));
    this.hidden = false;
  };
}

if (!customElements.get('bhg-delivery-estimate')) {
  customElements.define('bhg-delivery-estimate', BHGDeliveryEstimate);
}
